import { useCallback, useEffect, useState } from 'react';
import { downscale } from './downscale';
import { enqueue, newActionId, pending, startAutoSync, sync } from './offline-queue';

export type FieldView = {
  assignmentId: string;
  technician: { name: string };
  acknowledged: boolean;
  job: {
    reference: string;
    title: string;
    description: string | null;
    status: string;
    priority: string;
    jobType: string;
    area: string | null;
    site: {
      label: string;
      houseNo: string | null;
      landmark: string | null;
      accessType: string;
      accessNotes: string | null;
      lat: string | null;
      lng: string | null;
      contactName: string | null;
      contactPhone: string | null;
    } | null;
    customer: { name: string; phone: string; alt: string | null } | null;
    element: { name: string; type: string } | null;
    completionSchema: Array<{
      key: string;
      label: string;
      type: 'text' | 'number' | 'boolean';
      required?: boolean;
    }>;
    requiredEvidence: { photosMin?: number; signature?: boolean };
    materials: Array<{ code: string; quantity: number; serial: string | null; note: string | null }>;
    materialsOther: string | null;
    /** When the technician marked themselves on site, if they have. */
    onSiteAt: string | null;
  };
  actions: string[];
  /** The stock list travels with the job, so the checklist works offline. */
  materialCatalogue: Array<{
    code: string;
    name: string;
    aka: string | null;
    category: string;
    unit: 'each' | 'metre';
    requiresSerial: boolean;
    maxPerVisit: number | null;
  }>;
  categoryLabels: Record<string, string>;
};

/** Mirrors the office Attachment shape; kept here so the field bundle is standalone. */
export type FieldAttachment = {
  id: string;
  kind: string;
  mimeType: string;
  sizeBytes: number;
  caption: string | null;
  createdAt: string;
  uploadedBy: string | null;
  uploadedByType: string;
};

const cacheKey = (id: string) => `gsn-field-job:${id}`;

/**
 * The technician's job, offline-first.
 *
 * Reads come from the network when there is one and from the last cached copy
 * when there is not. Writes always go to the local queue first and are shown as
 * done immediately — the technician taps, it works, they walk away, and the
 * phone catches up on its own.
 */
export function useFieldJob(assignmentId: string) {
  const [view, setView] = useState<FieldView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(0);
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState<FieldAttachment[]>([]);
  /** Actions taken since the last successful load, shown optimistically. */
  const [doneLocally, setDoneLocally] = useState<string[]>([]);

  const refreshQueueCount = useCallback(async () => {
    setQueued((await pending()).filter((item) => item.assignmentId === assignmentId).length);
  }, [assignmentId]);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`/t/${assignmentId}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { message?: string };
        /*
         * The link is finished — revoked, expired, or the job moved to somebody
         * else. The saved copy goes with it; falling back to it here is what
         * kept a revoked link opening the job from this phone indefinitely.
         */
        if ([401, 403, 404, 410].includes(response.status)) {
          localStorage.removeItem(cacheKey(assignmentId));
          setView(null);
          setError(body.message ?? 'This job link is no longer valid.');
          return;
        }
        throw new Error(body.message ?? `HTTP ${response.status}`);
      }
      const data = (await response.json()) as FieldView;
      setView(data);
      setError(null);
      // Keep a copy so the next visit works at the gate with no signal.
      localStorage.setItem(cacheKey(assignmentId), JSON.stringify(data));
      setDoneLocally([]);

      // The list only, not the images. Metadata is a few hundred bytes and the
      // thumbnails are fetched by the browser as they scroll into view — on a
      // phone paying for its own data, that difference matters.
      void fetch(`/t/${assignmentId}/photos`, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : { attachments: [] }))
        .then((body: { attachments: FieldAttachment[] }) => setPhotos(body.attachments ?? []))
        // A job still works without its gallery; failing the whole load over a
        // photo list would be a poor trade.
        .catch(() => undefined);
    } catch (caught) {
      const cached = localStorage.getItem(cacheKey(assignmentId));
      if (cached) {
        setView(JSON.parse(cached) as FieldView);
        setError(null);
      } else {
        setError(
          navigator.onLine
            ? (caught as Error).message
            : 'No signal, and this job has not been opened here before.',
        );
      }
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    void load();
    void refreshQueueCount();

    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    const stop = startAutoSync(async () => {
      await refreshQueueCount();
      // Something reached the server, so the job has probably moved on.
      await load();
    });

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      stop();
    };
  }, [load, refreshQueueCount]);

  /**
   * Queue an action. Never awaits the network: the id is generated here, once,
   * so however many times it is retried the server treats it as one tap.
   */
  const act = useCallback(
    async (body: Record<string, unknown>) => {
      setBusy(true);
      try {
        await enqueue({
          id: newActionId(),
          assignmentId,
          kind: 'action',
          body,
        });
        setDoneLocally((current) => [...current, String(body['event'])]);
        await refreshQueueCount();

        if (navigator.onLine) {
          await sync();
          await refreshQueueCount();
          await load();
        }
      } finally {
        setBusy(false);
      }
    },
    [assignmentId, load, refreshQueueCount],
  );

  const addPhoto = useCallback(
    async (file: File) => {
      setBusy(true);
      try {
        const { dataUrl, mimeType } = await downscale(file);
        await enqueue({
          id: newActionId(),
          assignmentId,
          kind: 'photo',
          body: { data: dataUrl, mimeType },
        });
        await refreshQueueCount();

        if (navigator.onLine) {
          await sync();
          await refreshQueueCount();
          await load();
        }
      } finally {
        setBusy(false);
      }
    },
    [assignmentId, load, refreshQueueCount],
  );

  const syncNow = useCallback(async () => {
    setBusy(true);
    try {
      await sync();
      await refreshQueueCount();
      await load();
    } finally {
      setBusy(false);
    }
  }, [load, refreshQueueCount]);

  return { view, loading, error, online, queued, busy, photos, doneLocally, act, addPhoto, syncNow };
}
